import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import TimeInput from 'components/field/TimeInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE, ENUMS, SHIPPING_PARTNERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faPlug } from '@fortawesome/pro-solid-svg-icons';

import * as S from './DeliveryCompanySettings.styled';

const defaultColSpan = {
	md: 6
};

const fullColSpan = {
	sm: 12
};

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const safeParse = (value) => {
	try {
		if (!value) {
			throw new Error();
		}
		return JSON.parse(value);
	} catch {
		return {};
	}
};

const AddDeliveryCompanySettingsForm = NiceModal.create(({ id, modalName, mode }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	const [credentials, setCredentials] = useState(null);
	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				clientName: yup.string().nullable().required(),
				clientCompanyId: yup.number().nullable(),
				deliveryCompany: yup.string().nullable().required(),
				connectionName: yup.string().nullable().required(),
				connectionNameKey: yup.string().nullable(),
				deliverySLA: yup.number().nullable(),
				pickupSLA: yup.number().nullable(),
				doNotSendSMS: yup.boolean().nullable(),
				companyType: yup.string().nullable(),
				returnAvailable: yup.boolean().nullable(),
				returnMethod: yup.string().nullable(),
				pickupAllCities: yup.boolean().nullable(),
				contactName: yup.string().nullable(),
				dailyCapacity: yup.number().nullable(),
				pickupCutoffTime: yup.string().nullable(),
				pickupSchedule: yup.boolean().nullable(),
				serviceType: yup.string().nullable(),
				aggrementType: yup.string().nullable().required(),
				creditRefundPeriod: yup.number().nullable(),
				webhookToken: yup.string().nullable(),
				JsonString: yup.string().nullable().json(),
				integrationName: yup.string().nullable(),
				credentials: yup.object().nullable(),
				volumetricWeightRate: yup.string().nullable(),
				storeID: yup.number().nullable(),
				escalationEmail: yup.string().nullable()
			}),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			clientName: null,
			clientCompanyId: null,
			deliveryCompany: null,
			connectionName: null,
			connectionNameKey: null,
			deliverySLA: null,
			pickupSLA: null,
			doNotSendSMS: false,
			companyType: null,
			returnAvailable: false,
			returnMethod: null,
			pickupAllCities: false,
			contactName: null,
			dailyCapacity: null,
			pickupCutoffTime: null,
			pickupSchedule: false,
			serviceType: null,
			aggrementType: null,
			creditRefundPeriod: null,
			webhookToken: null,
			JsonString: null,
			integrationName: null,
			credentials: null,
			volumetricWeightRate: null,
			storeID: null,
			escalationEmail: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const { data } = await axiosPrivate.post(ADMIN.DELIVERY_COMPANY_SETTINGS.GET_DC_DETAILS, { id });
			let aggrementType = null;
			if (data.omniAggregator !== null || data.aggregator !== null) {
				if (data.omniAggregator) {
					aggrementType = 'OMNI_AGGREGATOR';
				} else if (data.aggregator) {
					aggrementType = 'AGGREGATOR';
				} else {
					aggrementType = 'INTEGRATOR';
				}
			}
			return {
				...defaultValues,
				...data,
				doNotSendSMS: data.donotsendSMS,
				pickupCutoffTime: data.pickupCutoffTime ? dayjs(data.pickupCutoffTime).format('HH:mm') : null,
				aggrementType,
				volumetricWeightRate: safeParse(data.JsonString)?.volumetricWeightRate ?? null,
				pickupLocationID: safeParse(data.JsonString)?.pickupLocationID ?? null
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm()
	});

	const {
		handleSubmit,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = async (data) => {
		try {
			const pickupCutoffTime = new Date();
			pickupCutoffTime.setHours(data.pickupCutoffTime?.split?.(':')[0]);
			pickupCutoffTime.setMinutes(data.pickupCutoffTime?.split?.(':')[1]);

			const apiPath =
				mode === 'edit'
					? ADMIN.DELIVERY_COMPANY_SETTINGS.UPDATE_DC
					: ADMIN.DELIVERY_COMPANY_SETTINGS.ADD_DC_SETTINGS;

			let isAggregator = null;
			if (data.aggrementType === 'AGGREGATOR') {
				isAggregator = true;
			} else if (data.aggrementType !== 'OMNI_AGGREGATOR') {
				isAggregator = false;
			}

			let isOmniAggregator = null;
			if (data.aggrementType === 'OMNI_AGGREGATOR') {
				isOmniAggregator = true;
			} else if (data.aggrementType !== 'AGGREGATOR') {
				isOmniAggregator = false;
			}

			const jsonStringResult = {
				...safeParse(watch('JsonString')),
				volumetricWeightRate: watch('volumetricWeightRate') || '',
				pickupLocationID: watch('pickupLocationID') || ''
			};
			credentials?.forEach((cred) => {
				if (watch(`credentials.${cred.fieldName}`)) {
					jsonStringResult[cred.fieldName] = watch(`credentials.${cred.fieldName}`);
				}
			});

			const response = await axiosPrivate.post(apiPath, {
				id,
				clientID: data.clientName,
				deliveryCompId: data.deliveryCompany,
				connectionName: data.connectionName,
				connectionNameKey: data.connectionNameKey,
				connectedCompanyId: data.clientCompanyId,
				serviceType: data.serviceType,
				isAggregator,
				isOmniAggregator,
				deliverySLA: data.deliverySLA,
				pickupSLA: data.pickupSLA,
				dailyCapacity: data.dailyCapacity,
				storeID: data.storeID,
				contactName: data.contactName,
				returnMethod: data.returnMethod,
				companyType: data.companyType,
				pickupCutoffTime: data.pickupCutoffTime ? pickupCutoffTime : null,
				creditRefundPeriod: data.creditRefundPeriod,
				webhookToken: data.webhookToken,
				donotsendSMS: data.doNotSendSMS,
				returnAvailable: data.returnAvailable,
				pickupAllCities: data.pickupAllCities,
				pickupSchedule: data.pickupSchedule,
				jsonString: JSON.stringify(jsonStringResult),
				escalationEmail: data.escalationEmail
			});

			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.DELIVERY_COMPANY_SETTINGS));
				showSuccess();
				handleCloseModal();
			} else {
				throw new Error();
			}
		} catch (error) {
			showError(
				error?.response?.data?.code && {
					message: error.response.data.code
				}
			);
			AppLogger.error(error);
		}
	};

	const modalTitle = useMemo(() => {
		switch (mode) {
			case 'add':
				return 'addDeliveryCompanySettings';
			case 'edit':
				return 'editDeliveryCompanySettings';
			default:
				return 'deliveryCompanySettingsDetails';
		}
	}, [mode]);

	const getFieldSchema = (required, value) => {
		if (typeof value === 'number') {
			return required ? yup.number().nullable().required() : yup.number().nullable();
		} else {
			return required ? yup.string().nullable().required() : yup.string().nullable();
		}
	};

	const createFormSchema = (data) => {
		const schema = {};
		const parents = {};
		const defVal = {};

		data.forEach(({ parent, fieldName, default: def, required }) => {
			if (parent) {
				if (!defVal[parent]) {
					defVal[parent] = {};
					parents[parent] = { shape: {} };
				}
				parents[parent].shape[fieldName] = getFieldSchema(required);
				defVal[parent][fieldName] = def ?? null;
			} else {
				schema[fieldName] = getFieldSchema(required);
				defVal[fieldName] = def ?? null;
			}
		});

		for (const i in parents) {
			schema[i] = yup.object().shape({ ...parents[i].shape });
		}
		return { defaultValues: defVal, schema: yup.object().shape({ ...schema }) };
	};

	useEffect(() => {
		if (credentials) {
			const credentialSchema = createFormSchema(credentials);
			formMethods.reset({
				...formMethods.watch(),
				credentials: {
					...credentialSchema.defaultValues,
					...safeParse(watch('JsonString'))
				}
			});
		}
	}, [credentials]);

	const getDeliveryCompanyData = async () => {
		const { data } = await axiosPrivate.post(SHIPPING_PARTNERS.GET_DELIVERY_COMPANY_FORM, {
			companyID: watch('deliveryCompany')
		});
		setCredentials(data?.credentials);
	};

	useEffect(() => {
		if (watch('deliveryCompany')) {
			getDeliveryCompanyData();
		}
	}, [watch('deliveryCompany')]);

	const tabs = useMemo(
		() => [
			{
				label: 'deliveryCompanyDetails',
				component: () => (
					<ModalContent>
						<DeliveryCompanyDetails isViewMode={isViewMode} isEditMode={mode === 'edit'} />
					</ModalContent>
				)
			},
			{
				label: 'credentialsDetails',
				component: () => (
					<ModalContent>
						<CredentialsDetails isViewMode={isViewMode} credentials={credentials} />
					</ModalContent>
				)
			}
		],
		[isViewMode, credentials]
	);

	return (
		<Modal fullwidth name={modalName} maxWidth="medium">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>

			<Form formMethods={formMethods}>
				<Tabs data={tabs} />
			</Form>

			<ModalActions>
				<Button label={isViewMode ? 'close' : 'cancel'} variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						disabled={!isDirty}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

const DeliveryCompanyDetails = ({ isViewMode, isEditMode }) => {
	const formMethods = useFormContext();
	const {
		setValue,
		watch,
		reset,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	return (
		<S.FormContainer>
			<FormItem colSpan={defaultColSpan} label="clientName" isLoading={isLoading} required>
				<AutoComplete
					autoCompleteName={AUTOCOMPLETE.ALL_CLIENTS}
					value={watch('clientName')}
					setValue={(value, isFirstRender) => {
						setValue('clientName', value?.id ?? null, validateOptions);
						if (isFirstRender) {
							reset({
								...watch(),
								clientCompanyId: value?.companyID ?? null
							});
						} else {
							setValue('clientCompanyId', value?.companyID ?? null, { shouldDirty: false });
						}
					}}
					name="clientName"
					columns={[
						{ accessor: 'Name' },
						{ accessor: 'id', width: 100 },
						{ accessor: 'companyID', width: 100 }
					]}
					selectionLabelAccessor={['Name', 'id', 'companyID']}
					viewMode={isViewMode}
					disabled={isEditMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="deliveryCompany" isLoading={isLoading} required>
				<AutoComplete
					autoCompleteName={AUTOCOMPLETE.ALL_ACTIVE_DELIVERY_COMPANIES}
					value={watch('deliveryCompany')}
					setValue={(value) => {
						setValue('deliveryCompany', value?.ID ?? null, validateOptions);
						setValue('integrationName', value?.integrationName ?? null, validateOptions);
					}}
					name="deliveryCompany"
					columns={[{ accessor: 'ID', width: 100 }, { accessor: 'Name' }]}
					selectionLabelAccessor={['Name']}
					viewMode={isViewMode}
					disabled={isEditMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="connectionName" isLoading={isLoading} required>
				<StringInput name="connectionName" placeholder="connectionName" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="connectionNameKey" isLoading={isLoading}>
				<StringInput name="connectionNameKey" placeholder="connectionNameKey" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="dcEscalationReceiverEmail" isLoading={isLoading}>
				<StringInput name="escalationEmail" placeholder="escalationEmail" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="serviceType" isLoading={isLoading}>
				<EnumSelect
					name="serviceType"
					placeholder="serviceType"
					enumName={ENUMS.DC_CONTRACT_SERVICE_TYPES}
					setValue={(value) => {
						setValue('serviceType', value, validateOptions);
					}}
					value={watch('serviceType')}
					viewMode={isViewMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="aggrementType" isLoading={isLoading} required>
				<EnumSelect
					name="aggrementType"
					placeholder="aggrementType"
					enumName={ENUMS.SHIPPING_PRICE_AGREEMENT_TYPE}
					setValue={(value) => {
						setValue('aggrementType', value, validateOptions);
					}}
					value={watch('aggrementType')}
					viewMode={isViewMode}
				/>
			</FormItem>

			<FormItem label="deliverySLA" colSpan={defaultColSpan} isLoading={isLoading}>
				<IntegerInput name="deliverySLA" allowFloat={false} showArrows={false} viewMode={isViewMode} />
			</FormItem>

			<FormItem label="pickupSLA" colSpan={defaultColSpan} isLoading={isLoading}>
				<IntegerInput name="pickupSLA" allowFloat={false} showArrows={false} viewMode={isViewMode} />
			</FormItem>

			<FormItem label="dailyCapacity" colSpan={defaultColSpan} isLoading={isLoading}>
				<IntegerInput name="dailyCapacity" allowFloat={false} showArrows={false} viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="contactName" isLoading={isLoading}>
				<StringInput name="contactName" placeholder="contactName" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="returnMethod" isLoading={isLoading}>
				<EnumSelect
					name="returnMethod"
					placeholder="returnMethod"
					enumName={ENUMS.RETURN_METHOD}
					setValue={(value) => {
						setValue('returnMethod', value, validateOptions);
					}}
					value={watch('returnMethod')}
					viewMode={isViewMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="companyType" isLoading={isLoading}>
				<EnumSelect
					name="companyType"
					placeholder="companyType"
					enumName={ENUMS.COMPANY_TYPE}
					setValue={(value) => {
						setValue('companyType', value, validateOptions);
					}}
					value={watch('companyType')}
					viewMode={isViewMode}
				/>
			</FormItem>

			<FormItem label="creditRefundPeriod" colSpan={defaultColSpan} isLoading={isLoading}>
				<IntegerInput name="creditRefundPeriod" allowFloat={false} showArrows={false} viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="pickupCutoffTime" isLoading={isLoading}>
				<TimeInput
					name="pickupCutoffTime"
					required
					setValue={(value) => {
						setValue('pickupCutoffTime', value, validateOptions);
					}}
					value={watch('pickupCutoffTime')}
					viewMode={isViewMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="pickupAllCities" isLoading={isLoading}>
				<SwitchInput name="pickupAllCities" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="returnAvailable" isLoading={isLoading}>
				<SwitchInput name="returnAvailable" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="pickupSchedule" isLoading={isLoading}>
				<SwitchInput name="pickupSchedule" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="doNotSendSMS" isLoading={isLoading}>
				<SwitchInput name="doNotSendSMS" viewMode={isViewMode} />
			</FormItem>
		</S.FormContainer>
	);
};

const CredentialsDetails = ({ isViewMode, credentials }) => {
	const axiosPrivate = useAxiosPrivate();
	const formMethods = useFormContext();
	const { showInfo, showError } = useAlert();
	const [formElems, setFormElems] = useState([]);

	const {
		setValue,
		watch,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	const generateToken = useCallback((length) => {
		let result = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		const array = new Uint32Array(characters.length);
		window.crypto.getRandomValues(array);
		for (let i = 0; i < length; i += 1) {
			result += characters[array[i] % characters.length];
		}
		return result;
	}, []);

	const formatJSON = (key) => {
		try {
			if (!watch(key)) {
				return '-';
			}
			return JSON.stringify(JSON.parse(watch(key)), null, 4);
		} catch (error) {
			AppLogger.log(error);
			return watch(key);
		}
	};

	const createFormElems = (data, formMethods) => {
		const { watch, setValue } = formMethods;
		return data.map(({ parent, fieldName, required, values }) => {
			const name = parent ? `${parent}.${fieldName}` : `${fieldName}`;
			if (values) {
				return (
					<FormItem key={fieldName} label={name} required={required} colSpan={defaultColSpan}>
						<EnumSelect
							data={values}
							value={watch(`credentials.${name}`)}
							setValue={(value) => {
								setValue(`credentials.${name}`, value, validateOptions);
								setValue(
									'JsonString',
									JSON.stringify({ ...safeParse(watch('JsonString')), [name]: value }),
									validateOptions
								);
							}}
							name={`credentials.${name}`}
							viewMode={isViewMode}
						/>
					</FormItem>
				);
			} else {
				return (
					<FormItem key={fieldName} label={fieldName} required={required} colSpan={defaultColSpan}>
						<StringInput
							name={`credentials.${name}`}
							maxlength="750"
							viewMode={isViewMode}
							onChange={(e) => {
								setValue(
									'JsonString',
									JSON.stringify({ ...safeParse(watch('JsonString')), [name]: e.target.value }),
									validateOptions
								);
							}}
						/>
					</FormItem>
				);
			}
		});
	};

	useEffect(() => {
		const jsonStringResult = safeParse(watch('JsonString'));
		if (Object.keys(jsonStringResult)?.length > 0) {
			credentials?.forEach((cred) => {
				if (watch(`credentials.${cred.fieldName}`)) {
					setValue(`credentials.${cred.fieldName}`, jsonStringResult[cred.fieldName], validateOptions);
				}
			});
		}
	}, [watch('JsonString')]);

	useEffect(() => {
		if (credentials) {
			setFormElems(createFormElems(credentials, formMethods));
		}
	}, [formMethods, credentials]);

	const testConnection = async () => {
		try {
			const requestJson = {
				name: watch('integrationName') || '',
				deliveryCompanyID: watch('deliveryCompany'),
				connectedCompanyId: watch('clientCompanyId'),
				...watch('credentials')
			};

			const response = await axiosPrivate.post(
				ADMIN.DELIVERY_COMPANY_SETTINGS.DC_SETTINGS_TEST_CONNECTION,
				requestJson
			);

			if (response.status === 200) {
				if (response.data.status === true) {
					showInfo({
						header: 'success',
						message: 'info.DCTestConnectionSuccess'
					});
				}
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: 'error',
				message: error.response.data.code
			});
		}
	};

	return (
		<S.FormContainer>
			{formElems}
			{credentials && (
				<FormItem colSpan={fullColSpan} isLoading={isLoading}>
					<Button icon={faPlug} label="testConnection" variant="black" onClick={testConnection} />
				</FormItem>
			)}

			<FormItem colSpan={fullColSpan} label="webhookToken" isLoading={isLoading} className="form-row-container">
				<StringInput name="webhookToken" placeholder="webhookToken" viewMode={isViewMode} />
				{!isViewMode && (
					<div>
						<Button onClick={() => setValue('webhookToken', generateToken(20))}>generateToken</Button>
					</div>
				)}
			</FormItem>

			{watch('webhookToken') && watch('integrationName') && (
				<S.FormArea>
					<FormItem colSpan={fullColSpan} label="url" isLoading={isLoading} className="form-row-container">
						<CopyComponent
							value={`https://app.tryoto.com/webhook/deliveryCompany?token=${watch('webhookToken')}&dc=${watch('integrationName')}`}
							className="copy-button"
						/>
					</FormItem>
				</S.FormArea>
			)}

			<FormItem colSpan={defaultColSpan} label="volumetricWeightRate" isLoading={isLoading}>
				<EnumSelect
					data={['3000', '4000', '5000', '6000']}
					value={watch('volumetricWeightRate')}
					setValue={(value) => {
						setValue(`volumetricWeightRate`, value, validateOptions);
					}}
					searchable={false}
					viewMode={isViewMode}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="pickupLocationID" isLoading={isLoading}>
				<StringInput name="pickupLocationID" viewMode={isViewMode} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="brandName" isLoading={isLoading}>
				<AutoComplete
					autoCompleteName={AUTOCOMPLETE.DC_SETTINGS_BRAND}
					value={watch('storeID')}
					setValue={(value) => {
						setValue('storeID', value?.ID ?? null, validateOptions);
					}}
					name="storeID"
					columns={[{ accessor: 'Name', width: 200 }, { accessor: 'ID' }]}
					selectionLabelAccessor={['Name', 'ID']}
					viewMode={isViewMode}
					filterObject={{ companyID: watch('clientCompanyId') }}
				/>
			</FormItem>

			<FormItem colSpan={fullColSpan} label="JsonString" isLoading={isLoading}>
				{!isViewMode ? (
					<TextArea
						name="JsonString"
						placeholder="{ }"
						rows="8"
						className="json-text-area"
						maxlength={4096}
					/>
				) : (
					<Typography>
						<pre className="pre-text">{formatJSON('JsonString')}</pre>
					</Typography>
				)}
			</FormItem>
		</S.FormContainer>
	);
};

export default AddDeliveryCompanySettingsForm;
